import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Search, Plus, Pencil, Trash2, Hand } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Badge } from '@meno/ui/components/ui/badge'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@meno/ui/components/ui/dialog'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import { TextInput } from '@meno/ui/components/shared/TextInput'
import {
  getStopScreens, createStopScreen, updateStopScreen, deleteStopScreen, publishStopScreen,
  getMedia,
} from '@meno/ui/lib/api'
import EmptyState from '@/components/EmptyState'
import { PublishDialog } from '@/components/PublishDialog'
import { playlistThumbUrl } from '@/lib/playlistThumb'
import PlaylistDialog, { type PlaylistMediaFile } from '@/components/PlaylistDialog'
import ViewToggle, { type ViewMode } from '@/components/ViewToggle'
import { ContentCard, ContentListRow, type CardMenuItem } from '@/components/ContentCard'
import { usePermissions } from '@meno/ui/hooks/usePermissions'

// A stop screen is an idle attract loop (images/videos/playlists) that shows on
// top of a screen's normal content until someone touches it. Rendered as the
// "Stop Screens" tab inside the Tablet page (which owns the page header), so
// this panel is header-less and self-contained.
type StopScreenRow = {
  id: number
  name: string
  media: { id: number; file_type?: string; url?: string }[]
  playlists: { id: number; name: string }[]
  screens: { id: number; name: string }[]
  created_at: string
}

export function StopScreensPanel() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const navigate = useNavigate()

  const [stopScreens, setStopScreens] = useState<StopScreenRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [view, setView] = useState<ViewMode>('grid')

  const [createOpen, setCreateOpen] = useState(false)
  // Media catalogue for the create dialog's picker — stop screens only take
  // images/videos (the backend rejects other types), so pre-filter here.
  const [allMedia, setAllMedia] = useState<PlaylistMediaFile[]>([])
  const [sendTarget, setSendTarget]     = useState<StopScreenRow | null>(null)
  const [renameTarget, setRenameTarget] = useState<StopScreenRow | null>(null)
  const [renameDraft, setRenameDraft]   = useState('')
  const [deleteTarget, setDeleteTarget] = useState<StopScreenRow | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    getStopScreens()
      .then(r => setStopScreens(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    getMedia(false, 'signage')
      .then(r => setAllMedia(
        (r.data as PlaylistMediaFile[]).filter(f =>
          f.file_type.startsWith('image') || f.file_type.startsWith('video')),
      ))
      .catch(() => {})
  }, [])

  // Create — same PlaylistDialog form playlists use (stop_screen variant):
  // name + image/video picks with per-item durations. The playlist-only
  // params (description / links / event / companions) are ignored.
  const handleCreate = async (
    name: string, _description: string,
    mediaIds: number[], mediaDurations: Record<number, number>,
    _linkIds: number[], _linkDurations: Record<number, number>,
    mediaOrder: Record<number, number>,
  ): Promise<void> => {
    try {
      const { data } = await createStopScreen({
        name,
        media_ids: mediaIds,
        media_durations: mediaDurations,
        media_order: mediaOrder,
      })
      setCreateOpen(false)
      toast.success(t('stopScreen.created', 'Stop screen created'))
      navigate(`/management/stop-screens/${data.id}`)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('stopScreen.createFailed', 'Failed to create stop screen'))
      throw err
    }
  }
  const commitRename = async () => {
    if (!renameTarget) return
    const next = renameDraft.trim()
    if (!next || next === renameTarget.name) { setRenameTarget(null); return }
    try {
      const { data } = await updateStopScreen(renameTarget.id, { name: next })
      setStopScreens(prev => prev.map(p => p.id === data.id ? data : p))
      toast.success(t('common.renamedSuccess', 'Renamed successfully'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setRenameTarget(null)
    }
  }
  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteStopScreen(deleteTarget.id)
      setStopScreens(prev => prev.filter(p => p.id !== deleteTarget.id))
      toast.success(t('common.deleted', 'Deleted'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setDeleteTarget(null)
    }
  }

  const filtered = stopScreens.filter(s =>
    !search.trim() || s.name.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      {/* Search + New action */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('contentHub.searchStopScreens', 'Search Stop Screens…')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ps-9 h-9 transition-colors focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:text-foreground"
          />
        </div>
        <ViewToggle mode={view} onChange={setView} />
        {can('playlists:create') && (
          <Button size="sm" className="gap-2 h-9 shrink-0" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4" />
            {t('contentHub.newStopScreen', 'New Stop Screen')}
          </Button>
        )}
      </div>

      {/* Grid — fills remaining height and scrolls; empty state centers. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-video w-full rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Hand}
            title={t('contentHub.stopScreensEmptyTitle', 'No stop screens yet')}
            description={t('contentHub.stopScreensEmpty', 'A stop screen is an idle attract loop (images/videos) that shows on top of your content until someone touches the screen.')}
          />
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filtered.map(ss => (
              <StopScreenCard
                key={ss.id}
                row={ss}
                thumb={playlistThumbUrl(ss.media as PlaylistMediaFile[])}
                onOpen={() => navigate(`/management/stop-screens/${ss.id}`)}
                onSend={() => setSendTarget(ss)}
                onRename={() => { setRenameTarget(ss); setRenameDraft(ss.name) }}
                onDelete={() => setDeleteTarget(ss)}
              />
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map(ss => (
              <StopScreenListRow
                key={ss.id}
                row={ss}
                thumb={playlistThumbUrl(ss.media as PlaylistMediaFile[])}
                onOpen={() => navigate(`/management/stop-screens/${ss.id}`)}
                onSend={() => setSendTarget(ss)}
                onRename={() => { setRenameTarget(ss); setRenameDraft(ss.name) }}
                onDelete={() => setDeleteTarget(ss)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Create dialog — the shared playlist form in its stop_screen variant,
          so creating either type feels identical (name + media + durations). */}
      {createOpen && (
        <PlaylistDialog
          variant="stop_screen"
          playlist={null}
          allMedia={allMedia}
          thumbCache={{}}
          onSave={handleCreate}
          onClose={() => setCreateOpen(false)}
        />
      )}

      <PublishDialog
        open={!!sendTarget}
        mode="publish"
        kind="stop_screen"
        publish={payload => publishStopScreen(sendTarget!.id, payload)}
        onClose={() => setSendTarget(null)}
        onPublished={() => { setSendTarget(null); load() }}
      />

      <Dialog open={!!renameTarget} onOpenChange={o => { if (!o) setRenameTarget(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('common.rename', 'Rename')}</DialogTitle></DialogHeader>
          <TextInput label={t('stopScreen.name', 'Name')} value={renameDraft} onChange={setRenameDraft} maxLength={150} lang="any" autoFocus />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRenameTarget(null)}>{t('common.cancel')}</Button>
            <Button disabled={!renameDraft.trim()} onClick={commitRename}>{t('common.save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('stopScreen.deleteTitle', 'Delete Stop Screen')}
        description={t('stopScreen.deleteDesc', 'This stop screen will be removed. The media and playlists it references are not deleted.')}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  )
}

// ── Stop screen card / row ───────────────────────────────────────────────
// Wrappers over the shared ContentCard/ContentListRow so stop screens carry
// the same footer contract as playlists and layouts (publish icon + 3-dot).
type StopScreenCardProps = {
  row: StopScreenRow
  thumb: string | null
  onOpen: () => void
  onSend: () => void
  onRename: () => void
  onDelete: () => void
}

function useStopScreenMenu({ onRename, onDelete }: Pick<StopScreenCardProps, 'onRename' | 'onDelete'>): CardMenuItem[] {
  const { t } = useTranslation()
  const { can } = usePermissions()
  return [
    ...(can('playlists:edit') ? [{ icon: Pencil, label: t('common.rename', 'Rename'), onClick: onRename }] : []),
    ...(can('playlists:delete') ? [{ icon: Trash2, label: t('common.delete', 'Delete'), onClick: onDelete, destructive: true }] : []),
  ]
}

function stopScreenSubtitle(row: StopScreenRow, t: ReturnType<typeof useTranslation>['t'], withItems = false) {
  const assigned = row.screens.length > 0
    ? t('contentHub.onScreens', '{{count}} screens', { count: row.screens.length })
    : t('contentHub.notAssigned', 'Not assigned')
  const itemCount = (row.media?.length ?? 0) + (row.playlists?.length ?? 0)
  return withItems && itemCount > 0
    ? `${assigned} · ${itemCount} ${t('common.items', 'items')}`
    : assigned
}

function StopScreenListRow({ row, thumb, onOpen, onSend, onRename, onDelete }: StopScreenCardProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const menu = useStopScreenMenu({ onRename, onDelete })
  return (
    <ContentListRow
      title={row.name}
      subtitle={stopScreenSubtitle(row, t, true)}
      thumb={thumb}
      icon={Hand}
      tintClassName="bg-stop-screen/10"
      iconClassName="text-stop-screen"
      onOpen={onOpen}
      onPublish={can('playlists:edit') ? onSend : undefined}
      menu={menu}
    />
  )
}

function StopScreenCard({ row, thumb, onOpen, onSend, onRename, onDelete }: StopScreenCardProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const menu = useStopScreenMenu({ onRename, onDelete })
  const itemCount = (row.media?.length ?? 0) + (row.playlists?.length ?? 0)
  return (
    <ContentCard
      title={row.name}
      subtitle={stopScreenSubtitle(row, t)}
      onOpen={onOpen}
      onPublish={can('playlists:edit') ? onSend : undefined}
      menu={menu}
      media={
        <div className="block w-full aspect-video bg-stop-screen/10 overflow-hidden relative">
          {thumb ? (
            <img src={thumb} alt={row.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-14 h-14 rounded-2xl bg-stop-screen flex items-center justify-center shadow-md">
                <Hand className="w-7 h-7 text-white" />
              </div>
            </div>
          )}
          {itemCount > 0 && (
            <Badge variant="secondary" className="absolute bottom-2 start-2 h-5 text-[10px] px-1.5">
              {itemCount} {t('common.items', 'items')}
            </Badge>
          )}
        </div>
      }
    />
  )
}
